import React, { useEffect, useMemo, useState } from 'react';
import { Refresh_time } from '../../../../../../utils/constants/refresh_time.js';
import {
  useApproveFjJobMutation,
  useArchiveFjJobMutation,
  useDeleteFjJobMutation,
  useDisapproveFjJobMutation,
  useGetFjJobsQuery,
} from '../../../../../../redux/services/fj_jobs.jsx';
import { useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { QuestionModal, Spinner } from '../../../../../common/index.jsx';
import { AppBarCMS } from '../../../../../basic/index.jsx';
import { routes } from '../../../../../../utils/helpers/ApiClient.jsx';
import { useClickOnJobMutation } from '../../../../../../redux/services/fj_click_stats.jsx';
import { useDeleteInterestMutation } from '../../../../../../redux/services/fj_interests.jsx';
import FjJobsTable from './FjJobsTable.jsx';
import {
  ROLE_FRI_JOBS_ADMIN,
  ROLE_FRI_JOBS_EXTERNAL_PARTNER,
  ROLE_FRI_JOBS_STUDENT,
} from '../../../../../../utils/constants/roles.js';
import { useToastMessages } from '../../../../../../hooks/useToastMessages';
import TableFilter from '../../../../../common/TableFilter/TableFilter.jsx';

const TABLE_FILTER_TITLE = 'Filter pracovných ponúk';

const FjJobsPage = () => {
  const user = useSelector((state) => state.auth.user);
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { deleteSuccess, successMessage } = useToastMessages();

  const [rowToBeApproved, setRowToBeApproved] = useState(null);
  const [rowToBeDisapproved, setRowToBeDisapproved] = useState(null);
  const [rowToBeArchived, setRowToBeArchived] = useState(null);
  const [rowToBeDeletedJob, setRowToBeDeletedJob] = useState(null);
  const [rowToBeDeletedInterest, setRowToBeDeletedInterest] = useState(null);

  const [approveJob] = useApproveFjJobMutation();
  const [disapproveJob] = useDisapproveFjJobMutation();
  const [archiveJob] = useArchiveFjJobMutation();
  const [deleteInterest, { isSuccess: deleteInterestSuccess }] =
    useDeleteInterestMutation();
  const [deleteJob, { isSuccess: deleteJobSuccess }] = useDeleteFjJobMutation();

  // Resolve a single role name among the three allowed roles
  const allowedRoles = [
    ROLE_FRI_JOBS_ADMIN,
    ROLE_FRI_JOBS_STUDENT,
    ROLE_FRI_JOBS_EXTERNAL_PARTNER,
  ];
  const role =
    user?.roles?.find((r) => allowedRoles.includes(r.name))?.name || null;

  const {
    data: jobsData,
    isLoading,
    refetch: refetchJobsData,
  } = useGetFjJobsQuery();
  // ClickStats
  const [eventClickOnJob] = useClickOnJobMutation();

  // selected list option for the table (waiting/approved/archived)
  const [selectedJobListOption, setSelectedJobListOption] = useState(
    localStorage.getItem(TABLE_FILTER_TITLE) || 'approved',
  );

  const pageTitle = t('Zoznam pracovných ponúk');

  useEffect(() => {
    document.title = pageTitle + ' - FRI.jobs';
  }, [pageTitle]); // Update when pageTitle changes (translation ready)

  // counts for filter badges
  const { approvedCount, waitingCount, archivedCount } = useMemo(() => {
    const all = jobsData || [];
    const approved = all.filter(
      (j) => !!j.approved_at && !j.archived_at,
    ).length;
    const waiting = all.filter((j) => !j.approved_at && !j.archived_at).length;
    const archived = all.filter((j) => !!j.archived_at).length;
    return {
      approvedCount: approved,
      waitingCount: waiting,
      archivedCount: archived,
    };
  }, [jobsData]);

  // filtered jobs according to selected option
  const filteredJobs = useMemo(() => {
    const all = jobsData || [];
    if (selectedJobListOption === 'waiting') {
      return all.filter((j) => !j.approved_at && !j.archived_at);
    }
    if (selectedJobListOption === 'archived') {
      return all.filter((j) => !!j.archived_at);
    }
    // approved
    return all.filter((j) => !!j.approved_at && !j.archived_at);
  }, [jobsData, selectedJobListOption]);

  // Auto-refresh jobsData every Refresh_time seconds
  useEffect(() => {
    const intervalId = setInterval(() => {
      refetchJobsData();
    }, Refresh_time);
    return () => clearInterval(intervalId);
  }, [refetchJobsData]);

  useEffect(() => {
    if (deleteJobSuccess) {
      deleteSuccess();
      refetchJobsData();
    }
  }, [deleteJobSuccess]);

  useEffect(() => {
    if (deleteInterestSuccess) {
      deleteSuccess();
      refetchJobsData();
    }
  }, [deleteInterestSuccess]);

  const handleDeleteClick = (id) => {
    deleteJob(id)
      .unwrap()
      .then(() => {
        deleteSuccess();
        refetchJobsData();
      });
  };

  const handleApproveJob = (id) => {
    approveJob(id)
      .unwrap()
      .then(() => {
        successMessage({
          message: t('Pracovná ponuka bola schválená!'),
        });
        refetchJobsData();
      });
  };

  const handleDisapproveJob = (id) => {
    disapproveJob(id)
      .unwrap()
      .then(() => {
        successMessage({
          message: t('Pracovná ponuka bola zamietnutá!'),
        });

        refetchJobsData();
      });
  };

  const handleArchiveJob = (id) => {
    archiveJob(id)
      .unwrap()
      .then(() => {
        successMessage({
          message: t('Pracovná ponuka bola archivovaná'),
        });
        refetchJobsData();
      });
  };

  const handleDeleteInterestClick = (id) => {
    deleteInterest(id)
      .unwrap()
      .then(() => {
        deleteSuccess();
        refetchJobsData();
      });
  };

  const onApproveClick = (rowToBeApproved) => {
    setRowToBeApproved(rowToBeApproved);
  };
  const onDisapproveClick = (rowToBeDisapproved) => {
    setRowToBeDisapproved(rowToBeDisapproved);
  };

  const onArchiveClick = (rowToBeArchived) => {
    setRowToBeArchived(rowToBeArchived);
  };

  const onDeleteInterestClick = (rowToBeDisapproved) => {
    setRowToBeDeletedInterest(rowToBeDisapproved);
  };

  return isLoading ? (
    <Spinner />
  ) : (
    <>
      <AppBarCMS
        title={pageTitle}
        backUrl={routes.cmsDashboard}
        backUrlTooltip={t('Nástenka')}
        createText={t('Pridať pracovnú ponuku')}
        createUrl={routes.cmsFriJobsJobCreate}
        permissionsBasic={'fri-jobs-jobs'}
      />

      <div style={{ margin: '10px' }}>
        {/* Move TableFilter here from table */}
        <TableFilter
          title={t(TABLE_FILTER_TITLE)}
          value={selectedJobListOption}
          setValue={setSelectedJobListOption}
          options={[
            { value: 'approved', label: t('Schválené') },
            {
              value: 'waiting',
              label: t('Čakajúce na schválenie'),
              count: waitingCount,
            },
            { value: 'archived', label: t('Archivované') },
          ]}
        />

        <FjJobsTable
          jobsData={filteredJobs}
          fromPublic={false}
          role={role}
          filterPanel={role !== ROLE_FRI_JOBS_STUDENT}
          selectedJobListOption={selectedJobListOption}
          onEditClick={(record) => {
            navigate(`${routes.cmsFriJobsJobEdit}/${record.id}`);
          }}
          onDeleteJobClick={(row) => {
            deleteJob(row.id);
          }}
          onDetailClick={(id) => {
            if (role === ROLE_FRI_JOBS_STUDENT) {
              eventClickOnJob({ fj_job_id: id })
                .unwrap()
                .then(() => {
                  navigate(`${routes.cmsFriJobsJobDetail}/${id}`, {
                    state: {
                      fromPublic: false,
                    },
                  });
                });
            } else {
              navigate(`${routes.cmsFriJobsJobDetail}/${id}`, {
                state: {
                  fromPublic: false,
                },
              });
            }
          }}
          onInterestedClick={(row) => {
            navigate(`${routes.cmsFriJobsJobInterested}/${row}`);
          }}
          onApproveClick={(row) => {
            onApproveClick(row.id);
          }}
          onDisapproveClick={(row) => {
            onDisapproveClick(row.id);
          }}
          onArchiveClick={(row) => {
            onArchiveClick(row.id);
          }}
          onDeleteInterestClick={(row) => {
            onDeleteInterestClick(row);
          }}
        />
      </div>

      <QuestionModal
        chosenPositive={() => deleteInterest(rowToBeDeletedInterest)}
        question={t('Naozaj sa chcete zrušiť svoj záujem o pracovnú ponuku?')}
        visible={rowToBeDeletedInterest !== null}
        yes={t('Áno')}
        toggle={() => setRowToBeDeletedInterest(null)}
        headerTitle={t('Zrušiť záujem o pracovnú ponuku')}
      />

      <QuestionModal
        chosenPositive={() => handleDeleteClick(rowToBeDeletedJob)}
        question={t(
          'Naozaj chcete zmazať túto pracovnú ponuku? Pozor! Operácia sa nedá vrátiť späť!',
        )}
        visible={rowToBeDeletedJob !== null}
        yes={t('Áno')}
        toggle={() => setRowToBeDeletedJob(null)}
        headerTitle={t('Zmazať pracovnú ponuku')}
      />

      <QuestionModal
        chosenPositive={() => handleApproveJob(rowToBeApproved)}
        question={t(
          'Naozaj chcete schváliť túto pracovnú ponuku? Pracovná ponuka sa presunie medzi schválené a bude viditeľná pre študentov.',
        )}
        visible={rowToBeApproved !== null}
        yes={t('Áno')}
        toggle={() => setRowToBeApproved(null)}
        headerTitle={t('Schváliť pracovnú ponuku')}
      />

      <QuestionModal
        chosenPositive={() => handleDisapproveJob(rowToBeDisapproved)}
        question={t(
          'Naozaj chcete neschváliť túto pracovnú ponuku? Pracovná bude uložená do archívu a nebude viditeľná pre študentov.',
        )}
        visible={rowToBeDisapproved !== null}
        yes={t('Áno')}
        toggle={() => setRowToBeDisapproved(null)}
        headerTitle={t('Neschváliť pracovnú ponuku')}
      />

      <QuestionModal
        chosenPositive={() => handleArchiveJob(rowToBeArchived)}
        question={t(
          t(
            'Naozaj chcete archivovať danú pracovnú ponuku? Pracovná ponuka bude presunutá do archívu a nebude viac ponúkaná na portáli FRI.jobs študentom.',
          ),
        )}
        visible={rowToBeArchived !== null}
        yes={t('Áno')}
        toggle={() => setRowToBeArchived(null)}
        headerTitle={t('Archivovať pracovnú ponuku')}
      />
    </>
  );
};

export default FjJobsPage;
